import React, { useState, useMemo, useCallback } from 'react';
import { ExternalLink, Settings, FileText } from 'lucide-react';
import { useComponentMetrics, getMetricObjectiveClassName, libelleTauxConversion, libelleConversions } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
/**
 * GA4PagesChart - Composant pour afficher les métriques par page d'atterrissage GA4
 * Structure: Landing Page avec métriques
 * Design identique au GA4DevicesChart avec pagination comme GA4GeographyChart
 */
const GA4PagesChart = ({
  pages = [],
  previousPages = [],
  compareEnabled = false,
  formatNumber,
  loading = false,
  conversionLabel = 'Conversions',
  summaryBounceRate = null,
  serverTotals = null,
  serverPreviousTotals = null,
  siteUrl = null,
  // Props pour la configuration des métriques
  accountId = 'global',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'ga4_pages',
  // Props pour les options avancées (conversions)
  token = null,
  propertyId = null,
  conversionConfig = null,
  onConversionConfigSave = null
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'sessions', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const ITEMS_PER_PAGE = 10;

  // Hook pour les métriques du composant Pages (réactif aux changements)
  const componentMetrics = useComponentMetrics('ga4_pages', accountId);

  // Fonction helper pour vérifier si une métrique est sélectionnée
  const isMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Ordre des métriques à afficher (depuis la config)
  const metricsOrder = componentMetrics.order || componentMetrics.metrics || [];

  // Configuration des colonnes du tableau
  const columnConfig = useMemo(() => ({
    visites: {
      key: 'sessions',
      label: 'Sessions',
      getValue: (item) => item.sessions || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.sessions || 0
    },
    visiteurs: {
      key: 'users',
      label: 'Utilisateurs',
      getValue: (item) => item.users || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.users || 0
    },
    rebond: {
      key: 'bounceRate',
      label: '% de Rebond',
      getValue: (item) => item.bounceRate || 0,
      format: (val) => `${formatNumber(val, 1)}%`,
      getPrevValue: (prev) => prev?.bounceRate || 0,
      inverted: true
    },
    pages_vues: {
      key: 'pageViews',
      label: 'Pages vues',
      getValue: (item) => item.pageViews || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.pageViews || 0
    },
    taux_conv: {
      key: 'conversionRate',
      label: libelleTauxConversion(conversionConfig, conversionLabel),
      getValue: (item) => item.sessions > 0 ? (item.conversions / item.sessions * 100) : 0,
      format: (val) => `${formatNumber(val, 2)}%`,
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.conversions / prev.sessions * 100) : 0
    },
    conv: {
      key: 'conversions',
      label: libelleConversions(conversionConfig, conversionLabel),
      getValue: (item) => item.conversions || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.conversions || 0
    },
    transactions: {
      key: 'transactions',
      label: 'Transactions',
      getValue: (item) => item.transactions || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.transactions || 0
    },
    chiffre_affaires: {
      key: 'totalRevenue',
      label: 'Chiffre d\'affaires',
      getValue: (item) => item.totalRevenue || 0,
      format: (val) => `${formatNumber(val, 2)}€`,
      getPrevValue: (prev) => prev?.totalRevenue || 0
    },
    taux_transaction: {
      key: 'transactionRate',
      label: '% Conversion (transactions)',
      getValue: (item) => item.sessions > 0 ? (item.transactions / item.sessions * 100) : 0,
      format: (val) => `${formatNumber(val, 2)}%`,
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.transactions / prev.sessions * 100) : 0
    },
    panier_moyen: {
      key: 'avgOrderValue',
      label: 'Panier moyen',
      getValue: (item) => item.transactions > 0 ? (item.totalRevenue || 0) / item.transactions : 0,
      format: (val) => `${formatNumber(val, 2)}€`,
      getPrevValue: (prev) => prev && prev.transactions > 0 ? (prev.totalRevenue || 0) / prev.transactions : 0
    },
    duree_moy: {
      key: 'avgSessionDuration',
      label: 'Durée moyenne',
      getValue: (item) => item.avgSessionDuration || 0,
      format: (val) => {
        const mins = Math.floor(val / 60);
        const secs = Math.floor(val % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
      },
      getPrevValue: (prev) => prev?.avgSessionDuration || 0
    },
    pages_visite: {
      key: 'pagesPerSession',
      label: 'Pages/Session',
      getValue: (item) => item.sessions > 0 ? (item.pageViews || 0) / item.sessions : 0,
      format: (val) => formatNumber(val, 1),
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.pageViews || 0) / prev.sessions : 0
    }
  }), [formatNumber, conversionLabel, conversionConfig]);


  // Fonction pour afficher une variation
  const renderVariation = makeRenderVariation({ fontSize: '11px', format: (v) => formatNumber(v, 1) });

  // Fonction pour extraire un titre lisible depuis le chemin
  const getPageTitle = (path) => {
    if (!path || path === '/' || path === '(not set)') return 'Accueil';

    // Nettoyer le chemin
    const cleanPath = path
      .replace(/^\//, '') // Enlever le slash initial
      .replace(/\/$/, '') // Enlever le slash final
      .replace(/\.html?$/i, '') // Enlever les extensions
      .replace(/[-_]/g, ' '); // Remplacer tirets/underscores par espaces

    // Prendre le dernier segment et le formater
    const segments = cleanPath.split('/');
    const lastSegment = segments[segments.length - 1] || 'Accueil';

    // Capitaliser chaque mot
    return lastSegment
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .substring(0, 50) + (lastSegment.length > 50 ? '...' : '');
  };

  // Construire l'URL complète pour le lien externe
  const getFullUrl = (path) => {
    if (!siteUrl || !path || path === '(not set)') return null;
    const baseUrl = siteUrl.replace(/\/$/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`;
  };

  // Handler de tri
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Reset à la première page lors du tri
  };

  // Données triées
  const sortedPages = useMemo(() => {
    if (!pages || !pages.length) return [];

    const sorted = [...pages];
    sorted.sort((a, b) => {
      let aValue, bValue;

      if (sortConfig.key === 'pageTitle' || sortConfig.key === 'pagePath') {
        aValue = a.pagePath || '';
        bValue = b.pagePath || '';
        return sortConfig.direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      // Calculer conversionRate à la volée si nécessaire
      if (sortConfig.key === 'conversionRate') {
        aValue = a.sessions > 0 ? (a.conversions / a.sessions * 100) : 0;
        bValue = b.sessions > 0 ? (b.conversions / b.sessions * 100) : 0;
      } else {
        aValue = Number(a[sortConfig.key]) || 0;
        bValue = Number(b[sortConfig.key]) || 0;
      }

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [pages, sortConfig]);

  // Pagination
  const totalPages = Math.ceil((sortedPages?.length || 0) / ITEMS_PER_PAGE);
  const paginatedPages = useMemo(() => {
    if (!sortedPages || !sortedPages.length) return [];
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedPages.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedPages, currentPage]);

  // Calculate totals (sur toutes les pages, pas seulement celles affichées)
  const totals = useMemo(() => {
    // Le serveur ne renvoie que les premières pages : ses totaux, eux, portent sur l'ensemble.
    // Les recalculer à partir des lignes affichées donnerait un total très en dessous du bandeau.
    if (serverTotals && serverTotals.sessions > 0) return serverTotals;

    if (!pages || !pages.length) return { pageViews: 0, users: 0, sessions: 0, bounceRate: 0, avgSessionDuration: 0, transactions: 0, totalRevenue: 0, transactionRate: 0, conversions: 0, conversionsValue: 0, conversionRate: 0 };

    const result = pages.reduce((acc, page) => ({
      pageViews: acc.pageViews + (page.pageViews || 0),
      users: acc.users + (page.users || 0),
      sessions: acc.sessions + (page.sessions || 0),
      bounceSum: acc.bounceSum + ((page.bounceRate || 0) * (page.sessions || 0)),
      durationSum: acc.durationSum + ((page.avgSessionDuration || 0) * (page.sessions || 0)),
      // E-commerce
      transactions: acc.transactions + (page.transactions || 0),
      totalRevenue: acc.totalRevenue + (page.totalRevenue || 0),
      // Conversions
      conversions: acc.conversions + (page.conversions || 0),
      conversionsValue: acc.conversionsValue + (page.conversionsValue || 0)
    }), { pageViews: 0, users: 0, sessions: 0, bounceSum: 0, durationSum: 0, transactions: 0, totalRevenue: 0, conversions: 0, conversionsValue: 0 });

    result.bounceRate = result.sessions > 0 ? result.bounceSum / result.sessions : 0;
    result.avgSessionDuration = result.sessions > 0 ? result.durationSum / result.sessions : 0;
    result.transactionRate = result.sessions > 0 ? (result.transactions / result.sessions * 100) : 0;
    result.conversionRate = result.sessions > 0 ? (result.conversions / result.sessions * 100) : 0;

    return result;
  }, [pages, serverTotals]);

  // Calcul des totaux de la période précédente pour comparaison
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousPages || !previousPages.length) return null;

    // Même règle que pour le total courant : la période de comparaison a elle aussi
    // son total calculé par GA4, sinon la variation compare deux choses différentes.
    if (serverPreviousTotals && serverPreviousTotals.sessions > 0) return serverPreviousTotals;

    const result = previousPages.reduce((acc, page) => ({
      pageViews: acc.pageViews + (page.pageViews || 0),
      users: acc.users + (page.users || 0),
      sessions: acc.sessions + (page.sessions || 0),
      bounceSum: acc.bounceSum + ((page.bounceRate || 0) * (page.sessions || 0)),
      durationSum: acc.durationSum + ((page.avgSessionDuration || 0) * (page.sessions || 0)),
      // E-commerce
      transactions: acc.transactions + (page.transactions || 0),
      totalRevenue: acc.totalRevenue + (page.totalRevenue || 0),
      // Conversions
      conversions: acc.conversions + (page.conversions || 0),
      conversionsValue: acc.conversionsValue + (page.conversionsValue || 0)
    }), { pageViews: 0, users: 0, sessions: 0, bounceSum: 0, durationSum: 0, transactions: 0, totalRevenue: 0, conversions: 0, conversionsValue: 0 });

    result.bounceRate = result.sessions > 0 ? result.bounceSum / result.sessions : 0;
    result.avgSessionDuration = result.sessions > 0 ? result.durationSum / result.sessions : 0;
    result.transactionRate = result.sessions > 0 ? (result.transactions / result.sessions * 100) : 0;
    result.conversionRate = result.sessions > 0 ? (result.conversions / result.sessions * 100) : 0;

    return result;
  }, [compareEnabled, previousPages, serverPreviousTotals]);

  if (loading) {
    return (
      <div className="campaigns-table-section">
        <div className="section-header">
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FileText size={18} /> Pages d'atterrissage</h4>
        </div>
        <div className="table-container">
          <div className="loading-message" style={{ textAlign: 'center', padding: '40px' }}>
            <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
            Chargement des pages d'atterrissage...
          </div>
        </div>
      </div>
    );
  }

  if (!pages || pages.length === 0) {
    return null;
  }

  return (
    <>
      <div className="section-header">
        <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FileText size={18} /> Pages d'atterrissage</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Pages GA4"
            />
          ) : (
            <button
              onClick={() => setIsVisible(!isVisible)}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <div className="table-container">
          <div className="campaigns-table-wrapper">
            <table className="devices-table" style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th onClick={() => handleSort('pagePath')} className="sortable" style={{ cursor: 'pointer', minWidth: '280px' }}>
                    Page {sortConfig.key === 'pagePath' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  {metricsOrder.map(metricKey => {
                    if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                    const col = columnConfig[metricKey];
                    return (
                      <th key={metricKey} onClick={() => handleSort(col.key)} className="sortable numeric" style={{ cursor: 'pointer' }}>
                        <span className="th-libelle">{col.label}</span> {sortConfig.key === col.key && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {paginatedPages.map((page, index) => {
                  const fullUrl = getFullUrl(page.pagePath);
                  const pageTitle = getPageTitle(page.pagePath);
                  const prev = compareEnabled && previousPages
                    ? previousPages.find(p => p.pagePath === page.pagePath)
                    : null;

                  return (
                    <tr key={`${page.pagePath}-${index}`}>
                      <td style={{ maxWidth: '350px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <div style={{
                            fontWeight: '600',
                            color: '#1f2937',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            {pageTitle}
                            {fullUrl && (
                              <a
                                href={fullUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Ouvrir la page"
                                style={{ color: '#6b7280', display: 'inline-flex' }}
                              >
                                <ExternalLink size={12} />
                              </a>
                            )}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: '#6b7280',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {page.pagePath}
                          </div>
                        </div>
                      </td>
                      {metricsOrder.map(metricKey => {
                        if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                        const col = columnConfig[metricKey];
                        const value = col.getValue(page);
                        const prevValue = prev ? col.getPrevValue(prev) : null;
                        const objectiveClass = getMetricObjectiveClassName(metricKey, value, 'ga4_pages', accountId);
                        return (
                          <td key={metricKey} className={`numeric ${objectiveClass}`}>
                            {col.format(value)}
                            {prev && renderVariation(calculateVariation(value, prevValue), col.inverted)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr className="total-row">
                  <td>
                    <strong>TOTAL (toutes pages)</strong>
                    <div style={{ fontSize: '0.75em', fontWeight: 'normal', color: '#6b7280' }}>
                      {pages.length} pages listées
                    </div>
                  </td>
                  {metricsOrder.map(metricKey => {
                    if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                    const col = columnConfig[metricKey];
                    // Cas spécial pour le taux de rebond qui peut utiliser summaryBounceRate
                    const value = metricKey === 'rebond'
                      ? (summaryBounceRate ?? totals.bounceRate ?? 0)
                      : col.getValue(totals);
                    const prevValue = previousTotals ? col.getPrevValue(previousTotals) : null;
                    const objectiveClass = getMetricObjectiveClassName(metricKey, value, 'ga4_pages', accountId);
                    return (
                      <td key={metricKey} className={`numeric ${objectiveClass}`}>
                        <strong>{col.format(value)}</strong>
                        {previousTotals && renderVariation(calculateVariation(value, prevValue), col.inverted)}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                Affichage {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, sortedPages.length)} sur {sortedPages.length} pages
              </div>
              <div className="pagination-buttons">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Précédent
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="ga4_pages"
        accountId={accountId}
        token={token}
        propertyId={propertyId}
        currentAdvancedConfig={conversionConfig}
        onAdvancedConfigSave={onConversionConfigSave}
      />
    </>
  );
};

export default GA4PagesChart;
